import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import {
  Box,
  IllustratedMessage,
  Text,
  type TableColumn,
  TableProvider,
  Table,
  TableBody,
  theme as lemonadeTheme
} from '@percent/lemonade'
import { CursorPagination } from '@percent/workplace-giving/common/components/CursorPagination/CursorPagination'
import { usePermission } from '@percent/workplace-giving/common/hooks/usePermission/usePermission'
import { AppRoute } from '@percent/workplace-giving/routing/AppRoute.enum'
import { MyCampaignsSkeleton } from './MyCampaignsSkeleton'
import { useMyCampaignsQuery } from './useMyCampaignsQuery'
import classes from './MyCampaigns.module.scss'
import { permission, scope } from '@percent/workplace-giving/common/hoc/withPermissions'
import { Permissions } from '@percent/workplace-giving/constants/permissions'
import { MyCampaignsTableRow } from './MyCampaignsTableRow/MyCampaignsTableRow'
import { CampaignActionsModalsProvider } from './Modals/CampaignActionsModalsProvider'
import { LinkButton } from '@percent/domain/giving'
import { useIsMobile } from '@percent/workplace-giving/common/hooks/useIsMobile/useIsMobile'

export function MyCampaigns() {
  const { t } = useTranslation()
  const isMobile = useIsMobile()
  const navigate = useNavigate()
  const has = usePermission()
  const givingCampaignEnabled = has(scope('giving_campaign'))

  const canCreateCampaign = has(permission(Permissions.CampaignsWrite))

  useEffect(() => {
    if (!givingCampaignEnabled || !canCreateCampaign) {
      navigate(AppRoute.HOME)
    }
  }, [givingCampaignEnabled, navigate, canCreateCampaign])

  const {
    data,
    isLoading,
    isError,
    isRefetching,
    fetchNextPage,
    fetchPreviousPage,
    hasNextPage,
    hasPreviousPage,
    refetch
  } = useMyCampaignsQuery()

  const columns = useMemo(
    (): TableColumn[] => [
      {
        id: t('workplace_giving.common.name'),
        props: isMobile ? undefined : { width: '30%' }
      },
      {
        id: t('workplace_giving.myCampaigns.dates'),
        props: isMobile ? undefined : { width: '30%' }
      },
      {
        id: t('workplace_giving.myCampaigns.groups'),
        props: isMobile ? undefined : { width: '20%' }
      },
      {
        id: t('workplace_giving.common.status'),
        props: isMobile ? undefined : { width: '13.33%' }
      },
      {
        id: t('workplace_giving.common.actions'),
        props: isMobile ? undefined : { width: '6.67%' }
      }
    ],
    [t, isMobile]
  )

  if (isError) {
    return (
      <Box className={classes.fullWidth}>
        <Text className={classes.UnexpectedErrorMessage}>{t('workplace_giving.errors.unexpected')}</Text>
      </Box>
    )
  }

  if (isLoading || isRefetching) {
    return <MyCampaignsSkeleton />
  }

  return (
    <CampaignActionsModalsProvider refetch={refetch}>
      <Box className={classes.fullWidth}>
        <Box className={classes.ButtonsRow}>
          <LinkButton to={AppRoute.CREATE_CAMPAIGN} variant="primary" size="small" shape="oval" stretch={isMobile}>
            {t('workplace_giving.campaigns.wizard.createCampaign')}
          </LinkButton>
        </Box>
        {data?.data.length !== 0 ? (
          <Box className={classes.TableWrapper}>
            <TableProvider
              isLoading={false}
              columns={columns}
              illustration="opportunities-frame"
              totalResults={null}
              emptyTableTitle=""
              emptyTableDescription=""
            >
              <Table>
                <TableBody>
                  {data?.data.map(row => (
                    <MyCampaignsTableRow key={row.id} campaign={row} />
                  ))}
                </TableBody>
              </Table>
            </TableProvider>
            <Box className={classes.p2}>
              <CursorPagination
                nextPage={fetchNextPage}
                previousPage={fetchPreviousPage}
                hasNextPage={hasNextPage}
                hasPreviousPage={hasPreviousPage}
                totalRecords={data?.totalResults}
              />
            </Box>
          </Box>
        ) : (
          <Box pt={lemonadeTheme.spacing.l}>
            <IllustratedMessage
              illustration="no-milestones"
              title={t('workplace_giving.myCampaigns.emptyTableTitle')}
              description={t('workplace_giving.myCampaigns.emptyTableDescription')}
            />
          </Box>
        )}
      </Box>
    </CampaignActionsModalsProvider>
  )
}
